import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import Cookies from 'js-cookie';
import api from '../../api/api';
import './candidates.css';

const Candidates = ({ onCandidateSelect }) => {
  const [candidates, setCandidates] = useState([]);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [loading, setLoading] = useState({
    candidates: false,
    videos: false
  });
  const [notification, setNotification] = useState({ show: false, type: '', message: '' });
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [currentVideo, setCurrentVideo] = useState({ url: '', type: '', title: '' });

  const { accessToken, refreshAccessToken } = useAuth();
  const navigate = useNavigate();

  const Notification = () => (
    <div className={`notification ${notification.type}`} style={{ top: '80px' }}>
      {notification.message}
      <button 
        onClick={() => setNotification({ ...notification, show: false })} 
        className="close-btn"
      >
        &times;
      </button>
    </div>
  );

  const showNotification = useCallback((type, message) => {
    setNotification({ show: true, type, message });
    const timer = setTimeout(() => setNotification(prev => ({ ...prev, show: false })), 5000);
    return () => clearTimeout(timer);
  }, []);

  const getValidAccessToken = useCallback(async () => {
    try {
      if (!accessToken) {
        return await refreshAccessToken();
      }
      return accessToken;
    } catch (error) {
      showNotification('error', 'Session expired. Please log in again.');
      navigate('/login');
      return null;
    }
  }, [accessToken, refreshAccessToken, navigate, showNotification]);

  const fetchCompanyId = useCallback(async () => {
    try {
      const validToken = await getValidAccessToken();
      if (!validToken) return null;

      const response = await api.post(
        '/server/companies/company',
        {},
        {
          headers: { Authorization: `Bearer ${validToken}` },
        }
      );
      const { id, candidates: companyCandidates } = response.data;
      Cookies.set('companyId', id, { expires: 7 });
      return { companyId: id, candidates: companyCandidates };
    } catch (error) {
      console.error('Error fetching company ID:', error);
      showNotification('error', 'Failed to load company data');
      return { companyId: null, candidates: [] };
    }
  }, [getValidAccessToken, showNotification]);

  const fetchVideoFiles = useCallback(async (companyId, candidateId, videoType, referenceProviderEmail, previousEmployerName) => {
    try {
      const validToken = await getValidAccessToken();
      if (!validToken) return null;

      let url = `/graph/${companyId}/${candidateId}/${videoType}/files`;
      
      if (videoType === 'reference') {
        const params = new URLSearchParams();
        if (referenceProviderEmail) params.append('referenceProviderEmail', referenceProviderEmail);
        if (previousEmployerName) params.append('previousEmployerName', previousEmployerName);
        url += `?${params.toString()}`;
      }

      const response = await api.get(
        url,
        { headers: { Authorization: `Bearer ${validToken}` } }
      );
      
      if (response.data && response.data.body) {
        if (typeof response.data.body === 'string' && response.data.body.startsWith('http')) {
          return { url: response.data.body, title: `${videoType} video` };
        }
        if (typeof response.data.body === 'object' && response.data.body.url) {
          return { url: response.data.body.url, title: response.data.body.title || `${videoType} video` };
        }
      }
      
      if (typeof response.data === 'string' && response.data.startsWith('http')) {
        return { url: response.data, title: `${videoType} video` };
      }
      
      return null;
    } catch (error) {
      console.error(`Error fetching ${videoType} video:`, error);
      throw error;
    }
  }, [getValidAccessToken]);

  const fetchReferenceVideos = useCallback(async (companyId, candidateId, candidate) => {
    try {
      if (!candidate.employers || candidate.employers.length === 0) {
        return [];
      }

      const referenceVideos = [];
      
      for (const employer of candidate.employers) {
        try {
          const referenceVideo = await fetchVideoFiles(
            companyId, 
            candidateId, 
            'reference',
            employer.referenceProviderEmail,
            employer.companyName
          );
          
          if (referenceVideo) {
            referenceVideos.push({
              ...referenceVideo,
              employer: employer.companyName,
              referenceProviderEmail: employer.referenceProviderEmail,
              type: 'reference'
            });
          }
        } catch (error) {
          console.error(`Error fetching reference video for employer ${employer.companyName}:`, error);
        }
      }

      return referenceVideos;
    } catch (error) {
      console.error('Error fetching reference videos:', error);
      return [];
    }
  }, [fetchVideoFiles]);

  const fetchCandidates = useCallback(async () => {
    setLoading(prev => ({ ...prev, candidates: true }));
    try {
      let companyId = Cookies.get('companyId');
      let companyCandidates = [];
  
      if (!companyId) {
        const data = await fetchCompanyId();
        companyId = data.companyId;
        companyCandidates = data.candidates;
      } else {
        const validToken = await getValidAccessToken();
        if (!validToken) return;
  
        const response = await api.post(
          '/server/companies/company',
          {},
          {
            headers: { Authorization: `Bearer ${validToken}` },
          }
        );
        companyId = response.data.id;
        Cookies.set('companyId', companyId, { expires: 7 });
        companyCandidates = response.data.candidates;
      }

      setCandidates(companyCandidates.map(candidate => ({
        ...candidate,
        formattedEmployer: formatEmployerInfo(candidate.employers),
        preScreeningVideo: null,
        referenceVideos: [],
        referenceStatus: candidate.referenceStatus || 'Not Available',
        preScreeningStatus: candidate.preScreeningStatus || 
                         (candidate.status && candidate.status.toLowerCase().includes('prescreen') 
                          ? capitalizeFirstLetter(candidate.status.split(':')[1].trim())
                          : 'Not Available')
    })));
    } catch (error) {
      console.error('Error fetching candidates:', error);
      showNotification('error', 'Failed to load candidates');
    } finally {
      setLoading(prev => ({ ...prev, candidates: false }));
    }
  }, [fetchCompanyId, getValidAccessToken, showNotification]);

  const fetchCandidateVideos = useCallback(async (candidate) => {
    setLoading(prev => ({ ...prev, videos: true }));
    try {
      const companyId = Cookies.get('companyId');
      
      const [preScreeningVideo, referenceVideos] = await Promise.all([
        fetchVideoFiles(companyId, candidate.id, 'prescreen').catch(() => null),
        fetchReferenceVideos(companyId, candidate.id, candidate).catch(() => [])
      ]);

      const preScreeningStatus = preScreeningVideo 
        ? 'Completed' 
        : (candidate.status && candidate.status.toLowerCase().includes('prescreen') 
            ? capitalizeFirstLetter(candidate.status.split(':')[1].trim())
            : (candidate.preScreeningStatus 
                ? capitalizeFirstLetter(candidate.preScreeningStatus)
                : 'Not Available'));

      const hasAnyReferenceVideo = referenceVideos.length > 0;
      const referenceStatus = hasAnyReferenceVideo 
        ? `${referenceVideos.length} Reference${referenceVideos.length > 1 ? 's' : ''} Available`
        : (candidate.status && candidate.status.toLowerCase().includes('reference') 
            ? capitalizeFirstLetter(candidate.status.split(':')[1].trim())
            : (candidate.referenceStatus 
                ? capitalizeFirstLetter(candidate.referenceStatus)
                : 'Not Available'));

      setSelectedCandidate({
        ...candidate,
        preScreeningVideo,
        referenceVideos,
        referenceStatus,
        preScreeningStatus,
        formattedEmployer: formatEmployerInfo(candidate.employers)
      });
    } catch (error) {
      console.error('Error fetching candidate videos:', error);
      showNotification('error', 'Failed to load candidate videos');
      
      // Fallback: set the candidate without videos if fetching fails
      setSelectedCandidate({
        ...candidate,
        preScreeningVideo: null,
        referenceVideos: [],
        referenceStatus: 'Not Available',
        preScreeningStatus: 'Not Available',
        formattedEmployer: formatEmployerInfo(candidate.employers)
      });
    } finally {
      setLoading(prev => ({ ...prev, videos: false }));
    }
  }, [fetchVideoFiles, fetchReferenceVideos, showNotification]);

  const handleSelectCandidate = useCallback(async (candidate) => {
    onCandidateSelect(true);
    setSelectedCandidate({
      ...candidate,
      preScreeningVideo: null,
      referenceVideos: [],
      referenceStatus: 'Loading...',
      preScreeningStatus: 'Loading...'
    });
    await fetchCandidateVideos(candidate);
  }, [fetchCandidateVideos]);

  const capitalizeFirstLetter = (string) => {
    return string.charAt(0).toUpperCase() + string.slice(1);
  };

  const handleRequestReference = useCallback(async (candidateId) => {
    try {
      const companyId = Cookies.get('companyId'); 
      const validToken = await getValidAccessToken(); 
      if (!validToken) return;

      const companyResponse = await api.post(
        '/server/companies/company',
        {},
        {
          headers: { Authorization: `Bearer ${validToken}` },
        }
      ); 

      const candidate = companyResponse.data.candidates.find(
        (c) => c.id === candidateId
      );

      if (!candidate) {
        throw new Error('Candidate not found');
      }

      const candidateEmail = candidate.email;
      const params = new URLSearchParams({
        candidateEmail,
        companyId,
        requestType: 'reference',
      });

      const paramsString = params.toString();
      const url = `/email/send-email${paramsString ? '?' + paramsString : ''}`;

      await api.get(url, {
        headers: {
          Authorization: `Bearer ${validToken}`,
          'Content-Type': 'application/json',
        },
      });

      showNotification('success', 'Reference request email sent successfully!');
    } catch (error) { 
      showNotification('error', 'An error occurred while sending the reference request');
    }
  }, [getValidAccessToken, showNotification]);

  const formatEmployerInfo = (employers) => {
    if (!employers || employers.length === 0) return 'N/A';
    
    return employers.map(employer => (
      <div key={`${employer.companyName}-${employer.title}`} className="employer-info">
        <div className="employer-company">{employer.companyName || 'Unknown Company'}</div>
        {employer.title && <div className="employer-role">({employer.title})</div>}
        {employer.referenceProviderEmail && (
          <div className="employer-reference">Referee: {employer.referenceProviderEmail}</div>
        )}
      </div>
    ));
  };

  const StatusBadge = ({ status }) => {
    const statusType = status?.toLowerCase().includes('completed') ? 'completed' :
                      status?.toLowerCase().includes('requested') ? 'requested' :
                      status?.toLowerCase().includes('scheduled') ? 'scheduled' :
                      status?.toLowerCase().includes('not available') ? 'unavailable' :
                      status?.toLowerCase().includes('available') ? 'completed' :
                      'unknown';
    
    return (
      <span className={`status-badge ${statusType}`}>
        {status || 'N/A'}
        {statusType === 'requested' && (
          <span className="status-tooltip" title="Video request has been sent to candidate">
            ℹ️
          </span>
        )}
      </span>
    );
  };

  const VideoModal = () => (
    <div className={`video-modal ${showVideoModal ? 'show' : ''}`}>
      <div className="modal-content">
        <div className="modal-header">
          <h3>
            {currentVideo.type} Video: {currentVideo.title}
          </h3>
          <div className="modal-actions">
            {currentVideo.url && (
              <button 
                onClick={() => {
                  const link = document.createElement('a');
                  link.href = currentVideo.url;
                  link.download = `${currentVideo.type}_${currentVideo.title}.mp4`;
                  link.click();
                }}
                className="download-btn"
                title="Download Video"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                  <polyline points="7 10 12 15 17 10"></polyline>
                  <line x1="12" y1="15" x2="12" y2="3"></line>
                </svg>
              </button>
            )}
            <button 
              className="close-btn"
              onClick={() => setShowVideoModal(false)}
            >
              &times;
            </button>
          </div>
        </div>
        <div className="video-wrapper">
          {currentVideo.url ? (
            <video 
              key={currentVideo.url} 
              controls 
              autoPlay
            >
              <source src={currentVideo.url} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          ) : (
            <div className="no-video">Video not available</div>
          )}
        </div>
      </div>
    </div>
  );

  const VideoButton = ({ video, status, videoType, employer }) => {
    const [isLoading, setIsLoading] = useState(false);
    const hasVideo = video && status?.toLowerCase() === 'completed';
    const videoTypeLabel = videoType === 'prescreen' ? 'Pre-screening' : 'Reference';

    const handleClick = async () => {
      if (!hasVideo) return;
      setIsLoading(true);
      try {
        setCurrentVideo({
          url: video.url,
          type: videoTypeLabel,
          title: employer ? `${employer.companyName} reference` : videoTypeLabel
        });
        setShowVideoModal(true);
      } catch (error) {
        showNotification('error', `Error loading ${videoTypeLabel} video: ${error.message}`);
      } finally {
        setIsLoading(false);
      }
    };

    return (
      <button
        className={`video-button ${!hasVideo ? 'disabled' : ''}`}
        onClick={handleClick}
        disabled={!hasVideo || isLoading}
        title={hasVideo ? `View ${videoTypeLabel} Video` : `${videoTypeLabel} Video Not Available`}
      >
        {isLoading ? (
          <span className="spinner"></span>
        ) : (
          <>
            <span className="icon">🎬</span>
            {hasVideo ? 'Watch Video' : 'Unavailable'}
          </>
        )}
      </button>
    );
  };

  const RequestReferenceButton = ({ candidateId }) => {
    const [isRequesting, setIsRequesting] = useState(false);

    const handleClick = async (e) => {
      e.stopPropagation();
      setIsRequesting(true);
      try {
        await handleRequestReference(candidateId);
      } finally {
        setIsRequesting(false);
      }
    };

    return (
      <button
        className="request-reference-btn"
        onClick={handleClick}
        disabled={isRequesting}
      >
        {isRequesting ? 'Sending...' : 'Request Reference'}
      </button>
    );
  };

  const CandidateDetails = ({ candidate }) => {
    return (
      <div className="candidate-details">
        {loading.videos ? (
          <div className="loading-container">
            <div className="spinner"></div>
            <p>Loading candidate videos...</p>
          </div>
        ) : (
          <>
            <div className="candidate-header">
              <div className="candidate-title">
                <h3>{`${candidate.firstName || ''} ${candidate.lastName || ''}`}</h3>
                <div className="candidate-meta">
                  <span className="meta-item">
                    <strong>Email:</strong> {candidate.email || 'N/A'}
                  </span>
                  <span className="meta-item">
                    <strong>Phone:</strong> {candidate.phoneNumber || 'N/A'}
                  </span>
                </div>
              </div>
              <button 
                className="back-button"
                onClick={() => {
                  setSelectedCandidate(null);
                  onCandidateSelect(false);
                  window.scrollTo({
                    top: 0,
                    behavior: 'smooth'
                  });
                }}
              >
                ← Back to List
              </button>
            </div>
            
            <div className="video-section">
              <h4 className="section-subtitle">Video Submissions</h4>
              <table className="video-table">
                <thead>
                  <tr>
                    <th className="serial-col">#</th>
                    <th>Video Type</th>
                    <th>Previous Employer</th>
                    <th>Title at Employer</th>
                    <th>Status</th>
                    <th className="action-col">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {candidate.employers?.length > 0 ? (
                    candidate.employers.map((employer, index) => {
                      const referenceVideo = candidate.referenceVideos.find(
                        v => v.employer === employer.companyName && 
                             v.referenceProviderEmail === employer.referenceProviderEmail
                      );
                      
                      const status = referenceVideo ? 'Completed' : 
                                    employer.referenceRequested ? 'Requested' : 'Not Available';
                      
                      return (
                        <tr key={`${employer.companyName}-${index}`}>
                          <td>{index + 1}</td>
                          <td>Reference Video</td>
                          <td>{employer.companyName || 'N/A'}</td>
                          <td>{employer.title || 'N/A'}</td>
                          <td>
                            <StatusBadge status={status} />
                          </td>
                          <td>
                            <div className="action-buttons">
                              <VideoButton 
                                video={referenceVideo} 
                                status={status}
                                videoType="reference"
                                employer={employer}
                              />
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td>1</td>
                      <td>Reference Video</td>
                      <td>N/A</td>
                      <td>N/A</td>
                      <td>
                        <StatusBadge status={candidate.referenceStatus} />
                      </td>
                      <td>
                        <div className="action-buttons">
                          <VideoButton 
                            video={null} 
                            status={candidate.referenceStatus}
                            videoType="reference"
                          />
                        </div>
                      </td>
                    </tr>
                  )}

                  <tr>
                    <td>{candidate.employers?.length ? candidate.employers.length + 1 : 2}</td>
                    <td>Pre-screening Video</td>
                    <td>N/A</td>
                    <td>N/A</td>
                    <td>
                      <StatusBadge status={candidate.preScreeningStatus} />
                    </td>
                    <td>
                      <VideoButton 
                        video={candidate.preScreeningVideo} 
                        status={candidate.preScreeningStatus}
                        videoType="prescreen"
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    );
  };

  useEffect(() => {
    if (!accessToken) {
      navigate('/userdashboard', { state: { scrollToTop: true } });
    } else if (candidates.length === 0) {
      fetchCandidates();
    }
  }, [accessToken, candidates.length, fetchCandidates, navigate]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [selectedCandidate]);

  return (
    <div className="candidates-container">
      {notification.show && <Notification />}
      <VideoModal />
      
      <div className="candidates-header">
        <h2>Candidate Management</h2>
        <div className="divider"></div>
      </div>
      
      {loading.candidates ? (
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading candidates...</p>
        </div>
      ) : !selectedCandidate ? (
        candidates.length > 0 ? (
          <div className="candidates-list-container">
            <div className="list-header">
              <h3>Candidate List</h3>
              <span className="count-badge">{candidates.length} candidates</span>
            </div>
            <div className="table-responsive">
              <table className="candidates-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {candidates.map((candidate, index) => (
                    <tr key={index} className="candidate-row">
                      <td>{index + 1}</td>
                      <td>
                        <button 
                          className="name-button"
                          onClick={() => handleSelectCandidate(candidate)}
                        >
                          <span className="candidate-name">
                            {`${candidate.firstName || ''} ${candidate.lastName || ''}`}
                          </span>
                        </button>
                      </td>
                      <td>{candidate.email}</td>
                      <td>
                      {(candidate.preScreeningStatus === "Completed" || 
        (candidate.status && candidate.status.toLowerCase().includes('prescreen:completed'))) && (
        <RequestReferenceButton candidateId={candidate.id} />
      )}
                        
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="no-candidates-message">
            <div className="empty-state-icon">📋</div>
            <h3>No Candidates Found</h3>
            <p>When you add candidates, they will appear here.</p>
          </div>
        )
      ) : (
        <CandidateDetails candidate={selectedCandidate} />
      )}
    </div>
  );
};

export default Candidates;